import axios from 'axios';
import { API_URL } from '../config/config.js';

// API基础配置 - 使用config.js配置
const API_BASE_URL = API_URL;

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 增加到5分钟，适应音频处理
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误和token刷新
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 如果是401错误且不是登录请求，处理token失效
    const isLoginRequest = originalRequest.url && originalRequest.url.includes('/login');
    if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          // TODO: 这里可以实现token刷新逻辑
          // const response = await apiClient.post('/auth/refresh', { refreshToken });
          // localStorage.setItem('accessToken', response.data.accessToken);
          // return apiClient(originalRequest);
          
          // 目前没有刷新接口，抛出错误直接走退出逻辑
          throw new Error('Token refresh not implemented');
        } else {
          throw new Error('No refresh token available');
        }
      } catch (refreshError) {
        // token过期或无效，清除本地状态并跳转到登录页
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user'); // 顺便清除 user 信息
        
        // 避免重复跳转
        if (window.location.pathname !== '/auth' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// API响应数据格式化
const handleResponse = (response) => {
  const data = response.data;

  // 如果响应已经包含success字段，直接返回
  if (data && typeof data === 'object' && 'success' in data) {
    return data;
  }

  // 否则包装成标准格式
  return {
    success: true,
    data: data,
    message: '请求成功'
  };
};

const handleError = (error) => {
  // 如果是特定的错误代码（如 404 并且是特定的业务异常），可以通过将错误原样返回或特殊标记处理
  // 为了让业务层能够捕获原始状态码，保留 response 属性
  const errorMessage = error.response?.data?.message || error.message || '请求失败';
  const errorCode = error.response?.status || 500;

  const errorObj = {
    message: errorMessage,
    code: errorCode,
    status: errorCode,
    response: error.response,
    errors: error.response?.data?.errors || []
  };

  throw errorObj;
};

export const api = {
  // 暴露 apiClient 供需要特殊处理（如 Blob）的场景使用
  client: apiClient,
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  },

  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return handleResponse(response);
    } catch (error) {
      throw handleError(error);
    }
  }
};

export default apiClient;
